import { useQueries } from "@tanstack/react-query";
import { apiClient } from "@/services/apiClient";

import { monthsTouched, type DayRange } from "../utils/dayAxis";
import type { SocialMediaReport } from "./useSocialMediaReport";

interface WindowReports {
  reports: SocialMediaReport[];
  isLoading: boolean;
}

// Outside the hook, so its reference holds and react-query re-runs it only when a month's result changes: the
// pips and Top posts built on its result are not rebuilt on every render.
const combine = (results: ReadonlyArray<{ data?: SocialMediaReport | null; isLoading: boolean }>): WindowReports => ({
  reports: results.flatMap((result) => (result.data ? [result.data] : [])),
  isLoading: results.some((result) => result.isLoading),
});

/** The stored social report of each month `range` touches, oldest first,
 *  for a window's post pips and Top posts. A month without a report yet
 *  ("Report not yet generated") is left out, never an error; a null range
 *  fetches nothing. Each month is cached on its own, so the next day's
 *  window reuses the months it shares. */
export const useWindowReports = (clientId: string, range: DayRange | null): WindowReports =>
  useQueries({
    queries: (range ? monthsTouched(range) : []).map(({ year, month }) => ({
      queryKey: ["social-report", clientId, year, month],
      queryFn: () =>
        apiClient
          .get<SocialMediaReport>(`/api/social-media/reports/${clientId}`, { params: { year, month } })
          .catch((err: unknown): null => {
            // The apiClient interceptor turns a 404 into a plain Error, so the message is what is left.
            if (err instanceof Error && err.message === "Report not yet generated") return null;
            throw err;
          }),
      enabled: Boolean(clientId),
      staleTime: 60_000,
    })),
    combine,
  });
